/* Google automated discounts on the product page. A Shopping-ad click brings Google's signed price in the pv2
   parameter; the app (App Proxy /apps/r69/gad) checks it. The price then shows for the selected variant while
   Google's token lives (an hour; Google asks for at least 30 minutes), and once that variant goes to the cart
   the app issues a one-time code that holds the price in the cart and at checkout for 48 hours. */
import { CartDiscountUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const KEY = 'r69-gad';
const node = document.getElementById('r69-gad-data');
const DATA = node ? JSON.parse(node.textContent || 'null') : null;

function read() {
  try {
    const all = JSON.parse(localStorage.getItem(KEY) || '{}');
    const out = {};
    for (const [v, e] of Object.entries(all)) if (e && e.exp * 1000 > Date.now()) out[v] = e;
    return out;
  } catch (e) {
    return {};
  }
}
function write(all) {
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch (e) {}
}

async function ask(path, body) {
  try {
    const r = await fetch('/apps/r69/gad/' + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return r.ok ? await r.json() : null;
  } catch (e) {
    return null;
  }
}

const vid = (id) => String(id || '').replace(/^.*\//, '');
const money = (x) => formatMoney(Math.round(x * 100), DATA.money, DATA.currency);

function selected() {
  const input = document.querySelector(`product-form-component[data-product-id="${DATA.product}"] input[name="id"]`);
  return vid((input && input.value) || new URL(location.href).searchParams.get('variant'));
}

function paint() {
  const all = read();
  const e = all[selected()];
  for (const el of document.querySelectorAll(`product-price[data-product-id="${DATA.product}"]`)) {
    const box = el.querySelector('[ref="priceContainer"]');
    if (!box) continue;
    if (!e) {
      if (box.dataset.r69Gad && box.dataset.r69Orig !== undefined) {
        box.innerHTML = box.dataset.r69Orig;
        delete box.dataset.r69Gad;
        delete box.dataset.r69Orig;
      }
      continue;
    }
    if (box.dataset.r69Gad === String(e.p)) continue;
    const regular = box.querySelector('.price__regular');
    const sale = box.querySelector('.price__sale');
    if (!regular || !sale) continue;
    if (box.dataset.r69Orig === undefined) box.dataset.r69Orig = box.innerHTML;
    box.dataset.r69Gad = String(e.p);
    regular.classList.add('price__hidden');
    sale.classList.remove('price__hidden');
    for (const n of sale.querySelectorAll('.price-item--sale')) n.textContent = money(e.p);
    for (const n of sale.querySelectorAll('.compare-at-price')) n.textContent = money(e.price);
  }
}

let later = 0;
function soon() {
  clearTimeout(later);
  later = setTimeout(paint, 0);
}

async function hold(v, e) {
  if (e.exp * 1000 <= Date.now()) return;
  const r = await ask('code', { t: e.t, product: e.product, country: DATA.country });
  if (!r || !r.ok || !r.code) return;
  const cart = await fetch(Theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' } }).then((x) => x.json());
  const codes = (cart.discount_codes || []).map((d) => d && d.code).filter(Boolean);
  if (codes.includes(r.code)) return;
  codes.push(r.code);
  const deferred = CartDiscountUpdateEvent.createPromise();
  document.dispatchEvent(new CartDiscountUpdateEvent({ discountCodes: codes.map((code) => ({ code })), promise: deferred.promise }));
  try {
    const res = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') }),
    });
    const data = await res.json();
    deferred.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data) });
  } catch (err) {
    deferred.reject(err);
  }
}

async function start() {
  const token = new URL(location.href).searchParams.get('pv2');
  if (token) {
    const r = await ask('check', { t: token, product: DATA.product, country: DATA.country });
    if (r && r.ok) {
      const all = read();
      all[vid(r.v)] = { t: token, p: r.p, pp: r.pp, price: r.price, exp: r.exp, product: String(r.product) };
      write(all);
    }
  }
  const all = read();
  if (!Object.values(all).some((e) => e.product === String(DATA.product))) return;

  paint();
  for (const el of document.querySelectorAll(`product-price[data-product-id="${DATA.product}"]`)) {
    new MutationObserver(soon).observe(el, { childList: true, subtree: true });
  }
  document.addEventListener('shopify:product:select', (event) => {
    if (event.promise) event.promise.then(soon, soon);
    else soon();
  });
  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (event.action !== 'add' || !event.promise) return;
    const now = read();
    const hit = (event.lines || []).map((l) => vid(l.merchandiseId)).find((v) => now[v]);
    if (!hit) return;
    event.promise
      .then((res) => {
        if (res && res.detail && res.detail.didError) return;
        return hold(hit, now[hit]);
      })
      .catch(() => {});
  });
  // the price leaves the page when Google's token expires
  const next = Math.min(...Object.values(all).map((e) => e.exp * 1000)) - Date.now();
  if (next > 0 && next < 2 * 3600 * 1000) setTimeout(function tick() {
    paint();
    const left = Object.values(read()).map((e) => e.exp * 1000 - Date.now());
    if (left.length) setTimeout(tick, Math.max(1000, Math.min(...left)));
  }, next + 1000);
}

if (DATA && DATA.product) start().catch(() => {});
